import { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  useFormContext,
  UseFormSetValue,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';
import { MdClose, MdDelete } from 'react-icons/md';
import { HERE_APIKEY, WAREHOUSE_IMAGES_URL } from '@constants';
import { Subheader } from '../../../tailwindComponents/Subheader';
import { Input } from '../../../tailwindComponents/Input';
import { MultiSelect } from '../../../tailwindComponents/MultiSelect';
import { Select } from '../../../tailwindComponents/Select';
import { Textarea } from '../../../tailwindComponents/Textarea';
import { Dropzone } from '../../../tailwindComponents/Dropzone';
import {
  CAPACITY_UOM,
  SERVICE_TIME_UNITS,
  TEMPERATURE_CONTROLS,
  SECTOR_EXPERIENCE,
  ACCREDITATIONS,
  TEMPERATURE_MONITORING,
  SYSTEMS,
  SAFETY_AND_SECURITY,
} from '../constants';
import { WarehouseFormValues } from '../types';
import { Autocomplete } from '../../../tailwindComponents/Autocomplete';

export function General({
  control,
  formState,
  setValue,
}: {
  control: Control<WarehouseFormValues>;
  formState: UseFormStateReturn<WarehouseFormValues>;
  setValue: UseFormSetValue<WarehouseFormValues>;
}) {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState<string | null>(null);
  const { getValues, clearErrors } = useFormContext();

  const images = useWatch({
    control,
    name: 'images',
  });

  const warehouseImages = useWatch({
    control,
    name: 'warehouseImages',
  });

  const watchedAccreditations = useWatch({
    control,
    name: 'accreditations',
  });
  const hasOtherAccreditation = !!watchedAccreditations?.some(
    (a) => a.value === 'Other'
  );

  useEffect(() => {
    if (!hasOtherAccreditation) {
      setValue('accreditationOther', '');
    }
  }, [hasOtherAccreditation, setValue]);

  const handleFetchLocationSuggestions = (input: string) => {
    fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${input}&at=52.93175,12.77165`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.items) {
          const items = data.items
            .filter((item: any) => item.address)
            .map(
              (item: {
                id: any;
                address: { label: string; city: string; countryName: string };
              }) => ({
                id: item.id,
                label: item.address.label,
                city: item.address.city,
                country: item.address.countryName,
              })
            );
          setOptions(items);
        }
      })
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
      });
  };

  const handleLocationOptionSelect = async (value: any) => {
    if (value && value.id) {
      setInputValue(value.label);
      setValue('address', value.label);
      setValue('city', value.city);
      setValue('country', value.country);
      clearErrors('address');
      try {
        const response = await fetch(
          `https://lookup.search.hereapi.com/v1/lookup?id=${value.id}&apiKey=${HERE_APIKEY}`
        );

        const data = await response.json();

        if (data) {
          const { position } = data;

          if (position) {
            const { lat, lng: lon } = position;

            const location = {
              lat,
              lon,
            };

            setValue('location', location);
            if (data.address?.postalCode) {
              setValue('postcode', data.address.postalCode);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching position information:', error);
      }
    }
  };

  const buildImagePreviewSrc = (image: any) => {
    return `${WAREHOUSE_IMAGES_URL}/${image.imageLocation}/small/${image.remoteName}`;
  };

  return (
    <>
      <Subheader subHeader='Initial Setup' />
      <div className='flex space-x-4'>
        <div className='flex flex-col space-y-4 grow'>
          <div className='flex space-x-4'>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <Input
                  label='Name'
                  {...field}
                  isInvalid={!!formState.errors.name}
                  validationMessage={formState.errors.name?.message as string}
                />
              )}
            />
            <Controller
              name='constructionYear'
              control={control}
              render={({ field }) => (
                <Input
                  label='Construction year'
                  {...field}
                  isInvalid={!!formState.errors.constructionYear}
                  validationMessage={
                    formState.errors.constructionYear?.message as string
                  }
                  optional
                />
              )}
            />
          </div>
          <Controller
            name='address'
            control={control}
            render={({ field }) => (
              <Autocomplete
                label='Location'
                options={options}
                value={inputValue ?? field.value}
                onInputChange={(newInputValue) => {
                  setInputValue(newInputValue);
                  handleFetchLocationSuggestions(newInputValue);
                }}
                onChange={(value) => {
                  handleLocationOptionSelect(value);
                }}
                isInvalid={!!formState.errors.address}
                validationMessage={formState.errors.address?.message as string}
              />
            )}
          />
          <Controller
            name='businessContactEmail'
            control={control}
            render={({ field }) => (
              <Input
                label='Corporate contact email'
                {...field}
                isInvalid={!!formState.errors.businessContactEmail}
                validationMessage={
                  formState.errors.businessContactEmail?.message as string
                }
                helperText='Used as the corporate contact for this warehouse'
              />
            )}
          />
          <div className='flex space-x-4'>
            <Controller
              name='experienceSectors'
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label='Experience sectors'
                  options={SECTOR_EXPERIENCE.map((sector) => ({
                    label: sector,
                    value: sector,
                  }))}
                  selected={field.value || []}
                  setSelected={field.onChange}
                  isInvalid={!!formState.errors.experienceSectors}
                  validationMessage={
                    formState.errors.experienceSectors?.message as string
                  }
                />
              )}
            />
            <Controller
              name='accreditations'
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label='Accreditations'
                  options={ACCREDITATIONS.map((accreditation) => ({
                    label: accreditation,
                    value: accreditation,
                  }))}
                  selected={field.value || []}
                  setSelected={field.onChange}
                  isInvalid={!!formState.errors.accreditations}
                  validationMessage={
                    formState.errors.accreditations?.message as string
                  }
                />
              )}
            />
          </div>
          {hasOtherAccreditation && (
            <Controller
              name='accreditationOther'
              control={control}
              render={({ field }) => (
                <Input
                  label='Other accreditation'
                  {...field}
                  isInvalid={!!formState.errors.accreditationOther}
                  validationMessage={
                    formState.errors.accreditationOther?.message as string
                  }
                />
              )}
            />
          )}
          <Controller
            name='description'
            control={control}
            render={({ field }) => (
              <Textarea label='Description' {...field} optional />
            )}
          />
        </div>
        <div className='flex flex-col space-y-4'>
          {images && images.length > 0 ? (
            <>
              <div className='block text-sm leading-5 font-medium text-gray-900 font-inter'>
                Image
                <span className='text-sm leading-5 font-normal font-inter text-gray-400 ml-2'>
                  Optional
                </span>
              </div>
              <div className='relative'>
                <img
                  src={buildImagePreviewSrc(images[0])}
                  alt={images[0].title || 'Warehouse image'}
                  className='object-contain max-h-60 max-w-full rounded'
                />
                <button
                  type='button'
                  onClick={() => {
                    const img = images[0];
                    setValue('deletedImagesIds', [
                      ...(getValues('deletedImagesIds') || []),
                      img.id,
                    ]);
                    setValue('images', []);
                  }}
                  aria-label='Delete image'
                  className='absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-7 h-7 flex items-center justify-center shadow transition'
                >
                  <MdDelete size={20} />
                </button>
              </div>
            </>
          ) : (
            <Dropzone
              label='Image'
              optional
              maxFileSizeMb={16}
              onDrop={(acceptedFiles: any) => {
                setValue('warehouseImages', [acceptedFiles]);
              }}
            />
          )}
        </div>
      </div>
      <Subheader subHeader='Characteristics and capacities' />
      <div className='flex space-x-4'>
        <Controller
          name='temperatureControl'
          control={control}
          render={({ field }) => (
            <Select
              label='Temperature control'
              options={TEMPERATURE_CONTROLS}
              selected={field.value}
              setSelected={field.onChange}
            />
          )}
        />
        <Controller
          name='temperatureMonitoring'
          control={control}
          render={({ field }) => (
            <MultiSelect
              label='Temperature monitoring'
              options={TEMPERATURE_MONITORING.map((monitoring) => ({
                label: monitoring,
                value: monitoring,
              }))}
              selected={field.value || []}
              setSelected={field.onChange}
              isInvalid={!!formState.errors.temperatureMonitoring}
              validationMessage={
                formState.errors.temperatureMonitoring?.message as string
              }
            />
          )}
        />
        <Controller
          name='systems'
          control={control}
          render={({ field }) => (
            <MultiSelect
              label='Systems'
              options={SYSTEMS.map((system) => ({
                label: system,
                value: system,
              }))}
              selected={field.value || []}
              setSelected={field.onChange}
              isInvalid={!!formState.errors.systems}
              validationMessage={formState.errors.systems?.message as string}
            />
          )}
        />
        <Controller
          name='safetyAndSecurity'
          control={control}
          render={({ field }) => (
            <MultiSelect
              label='Safety and security'
              options={SAFETY_AND_SECURITY.map((safety) => ({
                label: safety,
                value: safety,
              }))}
              selected={field.value || []}
              setSelected={field.onChange}
              isInvalid={!!formState.errors.safetyAndSecurity}
              validationMessage={
                formState.errors.safetyAndSecurity?.message as string
              }
            />
          )}
        />
      </div>
      <Subheader subHeader='Capacity' />
      <div className='flex space-x-4'>
        <div className='flex flex-col'>
          <Controller
            name='capacityUnitOfMeasure'
            control={control}
            render={({ field }) => (
              <Select
                label='Unit of measure'
                options={CAPACITY_UOM}
                selected={field.value}
                setSelected={field.onChange}
              />
            )}
          />
          <p className='mt-1 text-sm text-gray-500'>
            Choice will also determine the pricing mechanism
          </p>
        </div>
        <Controller
          name='maxStorageCapacity'
          control={control}
          render={({ field }) => (
            <Input
              label='Max storage capacity'
              {...field}
              isInvalid={!!formState.errors.maxStorageCapacity}
              validationMessage={
                formState.errors.maxStorageCapacity?.message as string
              }
            />
          )}
        />
        <Controller
          name='maxFLOXCapacity'
          control={control}
          render={({ field }) => (
            <Input
              label='Max FLOX capacity'
              {...field}
              isInvalid={!!formState.errors.maxFLOXCapacity}
              validationMessage={
                formState.errors.maxFLOXCapacity?.message as string
              }
              helperText='Available for shared use'
            />
          )}
        />
        <Controller
          name='remainingCapacity'
          control={control}
          render={({ field }) => (
            <Input
              label='Remaining capacity'
              {...field}
              isInvalid={!!formState.errors.remainingCapacity}
              validationMessage={
                formState.errors.remainingCapacity?.message as string
              }
              helperText='Available for new clients'
            />
          )}
        />
      </div>
      <Subheader subHeader='Terms' />
      <div className='flex space-x-4'>
        <Controller
          name='minCapacityRequirement'
          control={control}
          render={({ field }) => (
            <Input
              label='Min capacity requirement'
              {...field}
              isInvalid={!!formState.errors.minCapacityRequirement}
              validationMessage={
                formState.errors.minCapacityRequirement?.message as string
              }
            />
          )}
        />
        <Controller
          name='termsUnitOfMeasure'
          control={control}
          render={({ field }) => (
            <Select
              label='Unit of measure'
              options={SERVICE_TIME_UNITS}
              selected={field.value}
              setSelected={field.onChange}
            />
          )}
        />
        <Controller
          name='minTerm'
          control={control}
          render={({ field }) => (
            <Input
              label='Min term'
              {...field}
              isInvalid={!!formState.errors.minTerm}
              validationMessage={formState.errors.minTerm?.message as string}
            />
          )}
        />
      </div>
    </>
  );
}
